import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiConfigService } from '../../core/api-config/api-config.service';
import { HttpHandlerService } from '../../core/http-handler/http-handler.service';
import { LoggerService } from '../../core/logger/logger.service';
import { SessionStorageService } from '../../core/session-storage/session-storage.service';
import { getOccAuthTokenAPI, validateCaptchaAPI } from '../../shared/constants/end-point-constants';
import { captchaMasheryTokenKey, captchaOccTokenKey } from '../../shared/constants/storage-key-constants';
import { MasheryToken } from '../../shared/models/mashery-token.model';
import { OccToken } from '../../shared/models/occ-token.model';
import { SharedConfig } from '../../shared/models/shared-config.model';
import { sharedConfig } from '../../shared/shared.config';

declare global {
  interface Window {
    grecaptcha: any;
    reCaptchaLoad: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CaptchaHelperService {

  widgetId: number;
  tokensFetched: Subject<boolean> = new Subject<boolean>();
  isCaptchaReloading = false;
  constructor(
    private sessionStorage: SessionStorageService,
    private loggerService: LoggerService,
    private ngZone: NgZone,
    private httpHandlerService: HttpHandlerService,
    private apiConfigService: ApiConfigService,
    @Inject(sharedConfig) private config: SharedConfig) { }

  /**
   * @name initCaptcha
   * @description this method is use to add captcha script and register call back
   * @memberof CaptchaHelperService
   */
  initCaptcha() {
    if (this.widgetId === undefined) {
      this.registerReCaptchaCallback();
      this.addScript();
    } else {
      this.reloadCaptcha();
      this.loggerService.logInfo(`Captcha widget already exists in service`);
    }
  }

  /**
   * @name addScript
   * @description this method is used to add load captcha script
   * @memberof CaptchaHelperService
   */
  addScript() {
    if (document.getElementById('recaptcha-jssdk')) { return; }
    const script = document.createElement('script');
    script.id = 'recaptcha-jssdk';
    script.src = this.config.captchaScriptUrl;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }

  /**
   * @name registerReCaptchaCallback
   * @description this method is used to register callback for captcha script loaded successfully
   * @memberof CaptchaHelperService
   */
  registerReCaptchaCallback() {
    window.reCaptchaLoad = () => {
      this.ngZone.run(() => {
        this.executeInvisibleCaptcha();
      });
    };
  }

  /**
   *
   *
   * @name
   * @description Renders the container as a reCAPTCHA widget and returns the ID of the newly created widget.
   * @param element
   * @param config
   * @returns
   * @memberof CaptchaHelperService
   */
  render(element: HTMLElement, config: any): number {
    return window.grecaptcha.render(element, config);
  }

  /**
   * @name executeInvisibleCaptcha
   * @description This method renders captcha
   * @memberof CaptchaHelperService
   */
  executeInvisibleCaptcha() {
    if (this.widgetId === undefined) {
      const config = {
        sitekey: environment.masheryRegisterCaptchaSitekey,
        callback: this.onSuccess.bind(this),
        'expired-callback': this.onExpired.bind(this)
      };
      this.widgetId = this.render(document.getElementById('r-captcha'), config);
    } else {
      window.grecaptcha.reset(this.widgetId);
    }
    window.grecaptcha.execute(this.widgetId);
  }

  /**
   * @name onExpired
   * @description callback function for captcha expired
   * @memberof CaptchaHelperService
   */
  onExpired() {
    this.loggerService.logInfo('Captcha expired');
  }

  /**
   *
   *
   * @name onSuccess
   * @description callback function for captcha response success
   * @param token
   * @memberof CaptchaHelperService
   */
  onSuccess(token: string) {
    this.getTokens(token).pipe(retry(2)).subscribe((res: any) => {
      this.isCaptchaReloading = false;
      if (res) {
        this.tokensFetched.next(true);
      } else {
        this.tokensFetched.next(false);
      }
    }, err => {
      this.isCaptchaReloading = false;
      this.tokensFetched.next(false);
    });
  }

  /**
   *
   *
   * @name validateMasheryCaptcha
   * @description This method is used to get captcha token and get masheryToken
   * @param token
   * @returns
   * @memberof CaptchaHelperService
   */
  getTokens(token: string): Observable<any> {
    const authTokens$: Observable<any> = new Observable<any>(observer => {
      forkJoin(this.validateCaptcha(token), this.generateAuthToken(token))
        .subscribe((res: any[]) => {
          if (res && res.length === 2) {
            this.loggerService.logInfo(`Captcha : mashery & occ tokens fetched successfully.`);
            this.sessionStorage.setItem(captchaMasheryTokenKey, res[0]);
            this.sessionStorage.setItem(captchaOccTokenKey, res[1]);
            observer.next(true);
            observer.complete();
          } else {
            this.loggerService.logInfo(`Captcha : mashery & occ token NOT fetched.`);
            observer.error();
            observer.complete();
          }
        }, err => {
          this.loggerService.logInfo(`Captcha : mashery & occ token NOT fetched.`);
          observer.error();
          observer.complete();
        });
    });
    return authTokens$;
  }

  /**
   * @name reloadCaptcha
   * @description This methos is used to reload captcha and get tokens
   * Also, init captcha if script and callback not present
   * @memberof CaptchaHelperService
   */
  reloadCaptcha() {
    if (!this.isCaptchaReloading) {
      this.isCaptchaReloading = true;
      if (window.reCaptchaLoad) {
        window.reCaptchaLoad();
      } else {
        this.initCaptcha();
      }
    }
  }

  /**
   *
   *
   * @name validateCaptcha
   * @description mdms call to validate captcha token and get masheryToken
   * @param token
   * @returns
   * @memberof CaptchaHelperService
   */
  validateCaptcha(token: string) {
    const validateCaptcha$: Observable<any> = new Observable<any>(observer => {
      const api = this.apiConfigService.getAPIPath(validateCaptchaAPI);
      let urlSearchParams: HttpParams = new HttpParams();
      urlSearchParams = urlSearchParams.set('client_id', environment.masheryRegisterClientId);
      urlSearchParams = urlSearchParams.set('response', token);
      this.httpHandlerService.callServerPOST(api, urlSearchParams)
        .subscribe(response => {
          if (response && response.result) {
            observer.next(new MasheryToken(response.result));
            observer.complete();
          }
        }, err => {
          observer.error(err);
          observer.complete();
        });
    });
    return validateCaptcha$;
  }


  /**
   * method used to add class to hide captcha
   *
   * @memberof CaptchaHelperService
   */
  hideCaptcha() {
    const elem = document.getElementById('r-captcha');
    elem.classList.remove('show-captcha');
    elem.classList.add('hide-captcha');

  }

  /**
   * method used to add class to show captcha
   *
   * @memberof CaptchaHelperService
   */
  showCaptcha() {
    const elem = document.getElementById('r-captcha');
    elem.classList.remove('hide-captcha');
    elem.classList.add('show-captcha');

  }

  /**
   *
   *
   * @name generateAuthToken
   * @description get hybris auth token
   * @param token
   * @returns
   * @memberof CaptchaHelperService
   */
  generateAuthToken(token: string) {
    const authToken$: Observable<any> = new Observable<any>(observer => {
      const api = this.apiConfigService.getAPIPath(getOccAuthTokenAPI);
      let urlSearchParams: HttpParams = new HttpParams();
      urlSearchParams = urlSearchParams.set('client_id', environment.registrationCaptchaClientId);
      urlSearchParams = urlSearchParams.set('client_secret', environment.registrationCaptchaClientSecret);
      urlSearchParams = urlSearchParams.set('grant_type', 'client_credentials');
      urlSearchParams = urlSearchParams.set('scope', 'registration');
      urlSearchParams = urlSearchParams.set('siteId', environment.hybrisBaseSite);
      urlSearchParams = urlSearchParams.set('captchaResponse', token);

      this.httpHandlerService.callServerPOST(api, urlSearchParams)
        .subscribe((response) => {
          if (response) {
            observer.next(new OccToken(response));
            observer.complete();
          }
        }, err => {
          observer.error(err);
          observer.complete();
        });
    });
    return authToken$;
  }

}
